/**
 * RuntimeEnvironmentService
 * 
 * Detects the target environment at runtime based on the hostname.
 * Deployed hosts still map from hostname so a build can run on more than one environment.
 * Named Angular configurations (dev, qa, stg, production) bake environment.*.ts at compile time;
 * localhost keeps that compiled config instead of forcing STG.
 * 
 * This approach matches the TCC (Toyota Connected Services) pattern for environment-agnostic deployments.
 */

import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

// Shared constants for QA/STG environments
const SERVICE_DESK_URL = 'https://toyotaconnected.atlassian.net/servicedesk/customer/portal/26/group/250';
const QA_STG_TENANT_ID = '9107b728-2166-4e5d-8d13-d1ffdf0351ef';
const QA_STG_AUTHORITY = `https://login.microsoftonline.com/${QA_STG_TENANT_ID}`;

export interface EnvironmentConfig {
  envName: string;
  production: boolean;
  svpp_gateway: string;
  admin_guide_url: string;
  navi_portal_url: string;
  service_desk: string;
  adminportal_GW: string;
  us_config: {
    clientID: string;
    tenantID: string;
    authority: string;
    redirectUri: string;
    policy?: string;
    consentScopes?: string[];
    queryParams?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class RuntimeEnvironmentService {
  private get currentOrigin(): string {
    return this.getWindowLocation()?.origin || 'http://localhost:4200';
  }

  private readonly DEV_CONFIG: EnvironmentConfig = {
    envName: 'dev',
    production: false,
    svpp_gateway: 'https://ctportal.dev.telematicsct.com/apis',
    admin_guide_url: 'https://ssa.dev.telematicsct.com/ctportal/Admin-Portal-Features-07-21-2021.pdf',
    navi_portal_url: 'https://ctportal.dev.telematicsct.com/21MM/navi.html',
    service_desk: SERVICE_DESK_URL,
    adminportal_GW: 'https://ctportal.dev.telematicsct.com//adminportalapis/',
    us_config: {
      clientID: 'be371757-8814-463a-9eba-34bd666771d1',  // QA credentials for localhost dev
      tenantID: QA_STG_TENANT_ID,
      authority: QA_STG_AUTHORITY,
      redirectUri: 'https://ctportal.dev.telematicsct.com'
    }
  };

  private readonly QA_CONFIG: EnvironmentConfig = {
    envName: 'qa',
    production: false,
    svpp_gateway: 'https://ctportal.qa.telematicsct.com/apis',
    admin_guide_url: 'https://ssa.qa.telematicsct.com/ctportal/Admin-Portal-Features-07-21-2021.pdf',
    navi_portal_url: 'https://ctportal.qa.telematicsct.com/21MM/navi.html',
    service_desk: SERVICE_DESK_URL,
    adminportal_GW: 'https://ctportal.qa.telematicsct.com//adminportalapis/',
    us_config: {
      clientID: 'be371757-8814-463a-9eba-34bd666771d1',
      tenantID: QA_STG_TENANT_ID,
      authority: QA_STG_AUTHORITY,
      redirectUri: 'https://ctportal.qa.telematicsct.com',
      policy: '1Toyota-MFA-CloudApps-External',
      consentScopes: ['openid'],
      queryParams: 'p=1Toyota-MFA-CloudApps-External'
    }
  };

  private readonly STG_CONFIG: EnvironmentConfig = {
    envName: 'stg',
    production: false,
    svpp_gateway: 'https://ctportal.stg.telematicsct.com/apis',
    admin_guide_url: 'https://ssa.stg.telematicsct.com/ctportal/Admin-Portal-Features-07-21-2021.pdf',
    navi_portal_url: 'https://ctportal.stg.telematicsct.com/21MM/navi.html',
    service_desk: SERVICE_DESK_URL,
    adminportal_GW: 'https://ctportal.stg.telematicsct.com//adminportalapis/',
    us_config: {
      clientID: '890b9a4c-85d9-4cc3-aaa0-86b4810a7cf4',
      tenantID: QA_STG_TENANT_ID,
      authority: QA_STG_AUTHORITY,
      redirectUri: 'https://ctportal.stg.telematicsct.com',
      policy: '1Toyota-MFA-CloudApps-External',
      consentScopes: ['openid'],
      queryParams: 'p=1Toyota-MFA-CloudApps-External'
    }
  };

  private readonly PROD_CONFIG: EnvironmentConfig = {
    envName: 'prod',
    production: true,
    svpp_gateway: 'https://ctportal.telematicsct.com/apis',
    admin_guide_url: 'https://ssa.telematicsct.com/ctportal/Admin-Portal-Features-07-21-2021.pdf',
    navi_portal_url: 'https://ctportal.telematicsct.com/21MM/navi.html',
    service_desk: SERVICE_DESK_URL,
    adminportal_GW: 'https://ctportal.telematicsct.com//adminportalapis/',
    us_config: {
      clientID: '773910bb-e644-4ad9-a26d-eb6cbbef8991',
      tenantID: '8c642d1d-d709-47b0-ab10-080af10798fb',
      authority: 'https://login.microsoftonline.com/8c642d1d-d709-47b0-ab10-080af10798fb',
      redirectUri: 'https://ctportal.telematicsct.com',
      consentScopes: ['openid']
    }
  };

  constructor() {}

  /**
   * When an Angular configuration already replaced environment.ts (start:qa, build:stg, etc.),
   * keep that compile-time config instead of forcing STG on localhost.
   */
  private getCompiledEnvironmentConfig(): EnvironmentConfig | null {
    if (!environment.us_config?.clientID) {
      return null;
    }

    return {
      envName: environment.envName,
      production: environment.production,
      svpp_gateway: environment.svpp_gateway,
      admin_guide_url: environment.admin_guide_url,
      navi_portal_url: environment.navi_portal_url,
      service_desk: environment.service_desk,
      adminportal_GW: environment.adminportal_GW,
      us_config: { ...environment.us_config }
    };
  }

  /**
   * Detect environment from current hostname
   * Returns the appropriate config for the running environment
   */
  getEnvironmentConfig(): EnvironmentConfig {
    const hostname = this.getHostname();

    // Match hostname patterns to determine environment
    if (this.matchesPattern(hostname, /^(localhost|127\.0\.0\.1)$/)) {
      const compiled = this.getCompiledEnvironmentConfig();
      if (compiled) {
        return {
          ...compiled,
          us_config: {
            ...compiled.us_config,
            redirectUri: this.currentOrigin
          }
        };
      }
      return {
        ...this.STG_CONFIG,
        us_config: {
          ...this.STG_CONFIG.us_config,
          redirectUri: this.currentOrigin
        }
      };
    }

    if (this.matchesPattern(hostname, /^(ctportal\.qa\.|qa\.)/)) {
      return this.QA_CONFIG;
    }

    if (this.matchesPattern(hostname, /^(ctportal\.stg\.|stg\.)/)) {
      return this.STG_CONFIG;
    }

    if (this.matchesPattern(hostname, /^ctportal\.telematicsct\.com$/)) {
      return this.PROD_CONFIG;
    }

    // Default to DEV for unknown hostnames
    return this.DEV_CONFIG;
  }

  /**
   * Initialize environment object with runtime-detected values
   * Call this in AppComponent.ngOnInit() or AppInitializer
   */
  initializeEnvironment(): void {
    const config = this.getEnvironmentConfig();
    
    // Merge runtime config into environment object
    Object.assign(environment, config);
  }

  private getHostname(): string {
    try {
      return this.getWindowLocation()?.hostname || 'unknown';
    } catch (error) {
      return 'unknown';
    }
  }

  private getWindowLocation(): Location | undefined {
    return globalThis.location;
  }

  private matchesPattern(hostname: string, pattern: RegExp): boolean {
    return pattern.test(hostname);
  }
}
